import { X } from "lucide-react-native";
import { FC, ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  Heading,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

interface FormDialogProps {
  cancelLabel?: string;
  children: ReactNode;
  closeOnOverlayClick?: boolean;
  confirmDisabled?: boolean;
  confirmLabel: string;
  destructive?: boolean;
  error?: string | null;
  footerLeft?: ReactNode;
  headerExtra?: ReactNode;
  height?: number;
  hideCancel?: boolean;
  isOpen: boolean;
  loading?: boolean;
  loadingLabel?: string;
  maxWidth?: number;
  onCancel?: () => void;
  onClose: () => void;
  onConfirm: () => void;
  subtitle?: string;
  title: string;
}

const FormDialog: FC<FormDialogProps> = ({
  cancelLabel,
  children,
  closeOnOverlayClick = true,
  confirmDisabled,
  confirmLabel,
  destructive,
  error,
  footerLeft,
  headerExtra,
  height,
  hideCancel,
  isOpen,
  loading,
  loadingLabel,
  maxWidth = 560,
  onCancel,
  onClose,
  onConfirm,
  subtitle,
  title,
}) => {
  const { i18n } = useLocalization();

  return (
    <AlertDialog
      closeOnOverlayClick={closeOnOverlayClick}
      isOpen={isOpen}
      onClose={loading ? undefined : onClose}
    >
      <AlertDialogBackdrop />
      <AlertDialogContent
        className="gap-5 p-0"
        style={
          {
            height: height ? `min(${height}px, 90vh)` : undefined,
            maxHeight: "90vh",
            maxWidth,
            width: "100%",
          } as never
        }
      >
        <AlertDialogHeader className="px-6 pt-6 items-start">
          <VStack className="flex-1 gap-1">
            <Heading size="md">{title}</Heading>
            {subtitle && (
              <Text className="text-xs text-typography-400">{subtitle}</Text>
            )}
            {headerExtra && <VStack className="pt-3">{headerExtra}</VStack>}
          </VStack>
          <Pressable
            className="w-8 h-8 rounded-lg items-center justify-center hover:bg-background-100"
            disabled={loading}
            onPress={onClose}
          >
            <X color="#64748b" size={16} />
          </Pressable>
        </AlertDialogHeader>
        <AlertDialogBody
          className={`px-6 ${height ? "flex-1" : "flex-shrink"}`}
          contentContainerClassName="pb-1"
          style={{ minHeight: 0 } as never}
        >
          {children}
        </AlertDialogBody>
        <AlertDialogFooter className="px-6 py-4 border-t border-outline-100 bg-background-50">
          <HStack className="flex-1 items-center">
            {error ? (
              <Text className="text-error-600 text-sm">{error}</Text>
            ) : (
              footerLeft
            )}
          </HStack>
          {!hideCancel && (
            <Button
              className="rounded-xl"
              isDisabled={loading}
              onPress={onCancel ?? onClose}
              variant="outline"
            >
              <ButtonText>{cancelLabel ?? i18n.t("misc.cancel")}</ButtonText>
            </Button>
          )}
          <Button
            action={destructive ? "negative" : "primary"}
            className="rounded-xl"
            isDisabled={loading || confirmDisabled}
            onPress={onConfirm}
          >
            <ButtonText>
              {loading && loadingLabel ? loadingLabel : confirmLabel}
            </ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default FormDialog;
